export function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

export type ButtonVariant = "primary" | "secondary" | "tonal" | "danger" | "text";

export function renderButton(
  label: string,
  variant: ButtonVariant = "primary",
  disabled = false,
  attributes = "",
): string {
  const disabledAttribute = disabled ? ' disabled aria-disabled="true"' : "";
  const layout = DSBox({ className: `button button--${variant} ds-layout-token-radius`,
    radius: D.componentNativeWebLegacyButtonRadius, padding: DSLayoutInsets.axes(
    D.componentNativeWebLegacyButtonPaddingInline, D.componentNativeWebLegacyButtonPaddingBlock,
  ) });
  return `<button${layoutAttributes(layout)} type="button"${disabledAttribute} ${attributes}>${escapeHtml(label)}</button>`;
}

export function renderBadge(label: string, emphasized = false): string {
  const layout = combineLayoutRecipes(DSBox({ className: `badge${emphasized ? " badge--emphasized" : ""} ds-layout-token-radius`,
    radius: D.radiusPill,
    padding: DSLayoutInsets.axes(D.componentNativeWebLegacyBadgePaddingInline, D.componentNativeWebLegacyBadgePaddingBlock),
  }), DSInline({ align: "center" }));
  return `<span${layoutAttributes(layout)}>${escapeHtml(label)}</span>`;
}

function cardLayout(className: string): string {
  return layoutAttributes(combineLayoutRecipes(
    DSBox({ padding: DSLayoutInsets.all(D.componentNativeWebLegacyCardPadding),
      className: `${className} ds-layout-token-radius`, radius: D.componentNativeWebLegacyCardRadius }),
    DSStack({ gap: D.componentNativeWebLegacyCardGap, align: "stretch" }),
  ));
}

export function renderStatusCard(title: string, description: string, action?: string): string {
  const text = layoutAttributes(DSBox({ className: "ds-layout-margin-reset" }));
  return `
    <article${cardLayout("card stack-sm")}>
      <h3${text}>${escapeHtml(title)}</h3>
      <p${text}>${escapeHtml(description)}</p>
      ${action ? renderButton(action, "text") : ""}
    </article>
  `;
}

export function renderMetricCard(label: string, value: string, detail: string, progress?: number): string {
  const text = layoutAttributes(DSBox({ className: "ds-layout-margin-reset" }));
  const safeProgress = progress === undefined ? undefined : Number.isNaN(progress) ? 0 : Math.max(0, Math.min(100, progress));
  return `
    <article${cardLayout("card metric stack-sm")}>
      <span class="eyebrow">${escapeHtml(label)}</span>
      <strong>${escapeHtml(value)}</strong>
      <p${text}>${escapeHtml(detail)}</p>
      ${safeProgress === undefined ? "" : `
        <progress value="${safeProgress}" max="100" aria-label="${escapeHtml(label)}: ${safeProgress} %"></progress>
      `}
    </article>
  `;
}

export function renderProductCard(product: string, title: string, detail: string): string {
  const text = layoutAttributes(DSBox({ className: "ds-layout-margin-reset" }));
  return `
    <article${cardLayout("card product-card stack-sm")}>
      ${renderBadge(product)}
      <h3${text}>${escapeHtml(title)}</h3>
      <p${text}>${escapeHtml(detail)}</p>
    </article>
  `;
}
import { DSBox, DSInline, DSStack, combineLayoutRecipes } from "@mcebey/design-system/layout/recipes";
import { layoutAttributes } from "@mcebey/design-system/layout/html";
import { DSCoreDimension as D, DSLayoutInsets } from "@mcebey/design-system/layout/tokens";
